"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const masterclasses = [{
  day: "12",
  month: "JUL",
  weekday: "FRI",
  title: "Midnight Void Tour",
  master: "THE ECLIPSE",
  description: "The opening night of the summer stadium run. A high-energy pop spectacle under the stars.",
  duration: "2 HRS",
  level: "STADIUM",
  seats: "SOLD OUT",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_live_stadium.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_live_stadium.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_live_stadium.png-wvc-sizes"
}, {
  day: "15",
  month: "JUL",
  weekday: "MON",
  title: "Electric Magenta Live",
  master: "THE ECLIPSE",
  description: "An intimate club performance featuring acoustic renditions of the new album tracks.",
  duration: "1.5 HRS",
  level: "INTIMATE",
  seats: "12 LEFT",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_club_performance.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_club_performance.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_club_performance.png-wvc-sizes"
}, {
  day: "18",
  month: "JUL",
  weekday: "THU",
  title: "Neon Horizon Fest",
  master: "THE ECLIPSE",
  description: "Headlining the main stage at the world's largest independent pop music festival.",
  duration: "1 HR",
  level: "FESTIVAL",
  seats: "AVAILABLE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_festival_mainstage.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_festival_mainstage.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_festival_mainstage.png-wvc-sizes"
}, {
  day: "22",
  month: "JUL",
  weekday: "MON",
  title: "The Penumbra Session",
  master: "THE ECLIPSE",
  description: "Exclusive behind-the-scenes rehearsal and Q&A session for VIP fan club members.",
  duration: "3 HRS",
  level: "VIP ONLY",
  seats: "5 LEFT",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_rehearsal_bts.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_rehearsal_bts.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_rehearsal_bts.png-wvc-sizes"
}, {
  day: "25",
  month: "JUL",
  weekday: "THU",
  title: "Stellar Pulse Arena",
  master: "THE ECLIPSE",
  description: "A cinematic audio-visual experience featuring state-of-the-art laser choreography.",
  duration: "2 HRS",
  level: "ARENA",
  seats: "LIMITED",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_arena_lasers.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_arena_lasers.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_arena_lasers.png-wvc-sizes"
}];
export default function OnTheHorizon() {
  const [level, setLevel] = useState("all");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="5184" id="on-the-horizon" className="bg-background text-foreground pt-[120px] md:pt-[180px] pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.25em] uppercase mb-8 font-mono" style={{
            color: "var(--primary)"
          }}>
              Upcoming Tour Dates
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground">
              Witness the light.
              <br />
              Feel the{" "}
              <em className="italic font-bold" style={{
              color: "var(--primary)"
            }}>
                pulse
              </em>{" "}
              live.
            </h2>
          </div>

          {}
          <div className="lg:col-span-4 lg:pt-2">
            <div className="bg-card rounded-md p-5 shadow-md border border-border backdrop-blur-xl bg-opacity-15">
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md hover:bg-accent" aria-label="Previous month">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs tracking-[0.25em] uppercase text-foreground font-mono">
                  July 2025
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md hover:bg-accent" aria-label="Next month">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <ToggleGroup type="single" value={level} onValueChange={v => v && setLevel(v)} className="w-full grid grid-cols-4 gap-0 border border-border rounded-md overflow-hidden">
                <ToggleGroupItem value="all" className="text-[10px] tracking-[0.15em] uppercase h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground font-mono">
                  All
                </ToggleGroupItem>
                <ToggleGroupItem value="apprentice" className="text-[10px] tracking-[0.15em] uppercase h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground font-mono">
                  Club
                </ToggleGroupItem>
                <ToggleGroupItem value="journeyman" className="text-[10px] tracking-[0.15em] uppercase h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground font-mono">
                  Arena
                </ToggleGroupItem>
                <ToggleGroupItem value="master" className="text-[10px] tracking-[0.15em] uppercase h-9 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground font-mono">
                  Fest
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </div>

        {}
        <Separator className="mt-16 mb-2 bg-border" />

        {}
        <ul className="flex flex-col">
          {masterclasses.map((mc, i) => <li key={i} data-index={i} className="group relative border-b border-border last:border-b-0 transition-all duration-500 ease-out hover:bg-accent/10 hover:translate-x-2">
              {}
              <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />

              <div className="grid grid-cols-12 gap-4 md:gap-6 items-center py-8 md:py-6 md:min-h-[160px] px-2 md:px-4">
                {}
                <div className="col-span-3 md:col-span-2 flex flex-col">
                  <span className="font-default text-4xl md:text-5xl lg:text-[56px] leading-none text-foreground">
                    {mc.day}
                  </span>
                  <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase mt-2 font-mono" style={{
                color: "var(--muted-foreground)"
              }}>
                    {mc.month} — {mc.weekday}
                  </span>
                </div>

                {}
                <div className="col-span-3 md:col-span-2 lg:col-span-1">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-md overflow-hidden bg-muted">
                    <img src={mc.image} data-wvc-srcset={mc.srcset} data-wvc-sizes={mc.sizes} alt={mc.title} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                  </div>
                </div>

                {}
                <div className="col-span-6 md:col-span-4 lg:col-span-4">
                  <h3 className="font-default text-xl md:text-2xl text-foreground leading-tight">
                    {mc.title}
                  </h3>
                  <p className="text-[10px] md:text-xs tracking-[0.25em] uppercase mt-2 font-mono" style={{
                color: "var(--primary)"
              }}>
                    {mc.master}
                  </p>
                  <p className="hidden md:block text-sm md:text-base mt-3 leading-relaxed font-default" style={{
                color: "var(--muted-foreground)"
              }}>
                    {mc.description}
                  </p>
                </div>

                {}
                <div className="col-span-12 md:col-span-2 lg:col-span-3 flex md:flex-col gap-4 md:gap-1.5 pt-2 md:pt-0">
                  <span className="text-[10px] tracking-[0.25em] uppercase font-mono" style={{
                color: "var(--muted-foreground)"
              }}>
                    Set {mc.duration}
                  </span>
                  <span className="text-[10px] tracking-[0.25em] uppercase font-mono" style={{
                color: "var(--muted-foreground)"
              }}>
                    Type {mc.level}
                  </span>
                  <span className="text-[10px] tracking-[0.25em] uppercase font-mono" style={{
                color: "var(--muted-foreground)"
              }}>
                    Status {mc.seats}
                  </span>
                </div>

                {}
                <div className="col-span-12 md:col-span-12 lg:col-span-2 flex md:flex-col items-start md:items-end justify-start md:justify-center gap-3">
                  <Button asChild className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-10 text-sm tracking-wide font-default">
                    <Link to="/EclipsePage">Get Tickets</Link>
                  </Button>
                  <Link to="/EclipsePage" className="text-xs tracking-[0.2em] uppercase text-foreground underline-offset-4 hover:underline font-mono">
                    Info
                  </Link>
                </div>
              </div>
            </li>)}
        </ul>
      </div>
    </section>;
}